import { useLayoutEffect } from 'react';
import axios from 'axios';

export interface TenantConfig {
    /**
     * URL pattern to match (e.g., '/api/financing')
     */
    urlPattern: string;
    /**
     * The tenant ID value to set in X-ST-TenantId header
     */
    tenantId: string;
}

/**
 * Hook to set up an axios request interceptor that adds/overwrites X-ST-TenantId header
 * Supports multiple tenant ID values for different API endpoints
 *
 * This hook is designed to work with API clients that may have already set the X-ST-TenantId
 * header (potentially with an empty/undefined value). The interceptor will overwrite the header
 * with the correct tenant ID value.
 *
 * @param tenantConfigs - Array of configs mapping URL patterns to tenant IDs
 *
 * @example
 * // Single API with tenant ID
 * useAxiosTenantInterceptor([
 *   { urlPattern: '/api/financing', tenantId: '12345' }
 * ]);
 *
 * @example
 * // Multiple APIs with same tenant ID
 * useAxiosTenantInterceptor([
 *   { urlPattern: '/api/financing', tenantId: '12345' },
 *   { urlPattern: '/api/prequals', tenantId: '12345' }
 * ]);
 *
 * @example
 * // No tenant ID (or conditionally disabled)
 * useAxiosTenantInterceptor([]);
 */
export const useAxiosTenantInterceptor = (tenantConfigs?: TenantConfig[]) => {
    useLayoutEffect(() => {
        // Don't set up interceptor if no config or empty array
        if (!tenantConfigs || tenantConfigs.length === 0) {
            return;
        }

        // Add request interceptor
        const interceptorId = axios.interceptors.request.use(
            requestConfig => {
                // Match based on baseURL (API client sets this)
                const baseURL = requestConfig.baseURL ?? '';
                const matchingConfig = tenantConfigs.find(
                    ({ urlPattern }) => baseURL === urlPattern,
                );

                if (matchingConfig) {
                    // Always overwrite the X-ST-TenantId header since the API client may have
                    // set it with an empty/undefined value initially
                    requestConfig.headers['X-ST-TenantId'] = matchingConfig.tenantId;
                }

                return requestConfig;
            },
            error => {
                return Promise.reject(error);
            },
        );

        // Cleanup: eject the interceptor when component unmounts or config changes
        return () => {
            axios.interceptors.request.eject(interceptorId);
        };
    }, [tenantConfigs]);
};
